import { useState, useEffect } from "react";
import axiosService from "../../../../core/services/axios-service";
import { ApiRoutes } from "../../../../core/dictionary/api-url";
import ToastService from "../../../../core/services/toaster-service";
import { organisationActionType, organizationPlanEnum } from "../../../../core/dictionary/global-enums";
import Loader from "../../../../components/designs/loader";

export default function AddEditOrganization({
  type,
  organizationId,
  onSuccess,
}) {
  const isEdit = type === organisationActionType.edit ? true : false;
  const [form, setForm] = useState({
    organizationName: "",
    organizationAddress: "",
    organizationContactNumber: "",
    organizationEmail: "",
    organizationType: "",
    organizationPlan: "",
    organizationCoachseats: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch organization details if editing
  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      axiosService
        .get(`${ApiRoutes.ORGANIZATION_DETAILS}/${organizationId}`)
        .then((res) => {
          if (res.data && res.data.success && res.data.data) {
            setForm(res.data.data);
          } else {
            setError("Failed to fetch organization details.");
          }
        })
        .catch(() => setError("Server error. Please try again."))
        .finally(() => setLoading(false));
    } else {
      setForm({
        organizationName: "",
        organizationAddress: "",
        organizationContactNumber: "",
        organizationEmail: "",
        organizationType: "",
        organizationPlan: "",
        organizationCoachseats: null,
      });
    }
  }, [organizationId, isEdit]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if(typeof(form.organizationCoachseats) === "string") {
        form.organizationCoachseats = parseInt(form.organizationCoachseats);
    }
    try {
      let res;
      if (isEdit) {
        res = await axiosService.put(
          `${ApiRoutes.ORGANIZATION_EDIT}/${organizationId}`,
          form
        );
      } else {
        res = await axiosService.post(ApiRoutes.ORGANIZATION_CREATE, form);
      }
      if (res.data && res.data.success) {
        if (onSuccess) {
          ToastService.success(
            isEdit
              ? "Organization updated successfully."
              : "Organization added successfully."
          );
          onSuccess(res.data);
        }
      } else {
        setError(res.data.message || "Failed to save organization.");
      }
    } catch (err) {
      setError("Server error. Please try again.");
    }
    setLoading(false);
  };

  return (
    <>
      {!(loading && isEdit) ? (
        <form onSubmit={handleSubmit} className="p-3">
          <div className="mb-3">
            <label className="form-label">Organization Name</label>
            <input
              type="text"
              name="organizationName"
              className="form-control"
              placeholder="Enter organization name"
              value={form.organizationName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Address</label>
            <input
              type="text"
              name="organizationAddress"
              className="form-control"
              placeholder="Enter organization address"
              value={form.organizationAddress}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Contact Number</label>
            <input
              type="text"
              name="organizationContactNumber"
              className="form-control"
              placeholder="Enter organization contact number"
              value={form.organizationContactNumber}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="organizationEmail"
              className="form-control"
              placeholder="Enter organization email"
              value={form.organizationEmail}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Type</label>
            <select
              name="organizationType"
              className="form-select"
              placeholder="Select organization type"
              value={form.organizationType}
              onChange={handleChange}
              required
            >
              <option value="">Select Type</option>
              <option value="hospital">Hospital</option>
              <option value="clinic">Clinic</option>
              <option value="ngo">NGO</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Plans</label>
            <select
              name="organizationPlan"
              className="form-select"
              placeholder="Select organization plan"
              value={form.organizationPlan}
              onChange={handleChange}
              required
            >
              <option value="">Select Plan</option>
              {Object.entries(organizationPlanEnum).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Number of Coach</label>
            <input
              type="number"
              name="organizationCoachseats"
              className="form-control"
              value={form.organizationCoachseats}
              onChange={(e) => {
                // Only allow numbers
                const val = e.target.value.replace(/[^0-9]/g, "");
                setForm({ ...form, organizationCoachseats: val });
              }}
              required
              min="1"
              placeholder="Enter number of coach seats"
            />
          </div>
          {error && <div className="text-danger mb-2">{error}</div>}
          <button type="submit" className="btn btn-gradient" disabled={loading}>
            {loading
              ? isEdit
                ? "Updating..."
                : "Saving..."
              : isEdit
              ? "Update"
              : "Add"}
          </button>
        </form>
      ) : (
        <Loader text="Loading organization details..." />
      )}
    </>
  );
}
